import React from 'react';
import { CheckCircle2, Ticket, Receipt, Wallet, Clock3, Info } from 'lucide-react';
import './VoucherBudgetTab.css';

// ================== DATA (استبدلها بالداتا من الـ API) ==================
const budgetReview = [
  { label: 'Total Budget', value: '100,000 EGP' },
  { label: 'Consumed Budget', value: '35,000 EGP' },
  { label: 'Remaining Budget', value: '65,000 EGP' },
  { label: 'Utilization', value: '35%' },
];

const consumption = { used: '35,000 EGP', total: '100,000 EGP', pct: 35 };

const fundingAllocation = [
  { label: 'Platform (ESTBN)', value: '70,000 EGP', color: '#14B8A6' },
  { label: 'Provider', value: '30,000 EGP', color: '#4B5563' },
];
const fundingTotal = '100,000';

const redemptionSummary = [
  { icon: Ticket, iconBg: '#D1FAE5', iconColor: '#04B22E', label: 'Total Redemptions', value: '230' },
  { icon: Wallet, iconBg: '#F3F4F6', iconColor: '#6B7280', label: 'Total Discount Given', value: '35,000 EGP' },
  { icon: Receipt, iconBg: '#DBEAFE', iconColor: '#3E9FF6', label: 'Avg. Discount', value: '152 EGP' },
  { icon: Clock3, iconBg: '#FEF3C7', iconColor: '#FEB91E', label: 'Remaining Redemptions', value: '770' },
];

const chartLabels = ['Apr 21', 'Apr 25', 'Apr 29', 'May 03', 'May 07', 'May 11', 'May 15', 'May 19'];
const chartValues = [5, 12, 18, 22, 28, 33, 38, 45];

const fundingSplit = [
  { label: 'Platform (ESTBN)', value: '70,000 EGP', pct: '(70%)', color: '#14B8A6' },
  { label: 'Provider', value: '30,000 EGP', pct: '(30%)', color: '#4B5563' },
];
const fundingSplitTotal = { value: '100,000 EGP', pct: '(100%)' };

const budgetRules = [
  { label: 'Budget Type', value: 'Fixed Budget' },
  { label: 'Budget Limit', value: '100,000 EGP' },
  { label: 'Auto Pause', value: 'Enabled', pill: true },
  { label: 'Pause Threshold', value: '95%' },
  { label: 'Allow Over Budget', value: 'No' },
  { label: 'Budget Rollover', value: 'No' },
];

// ================== SUB COMPONENTS ==================
function StepBadge({ n }) {
  return <span className="vb-step-badge">{n}</span>;
}

function LineChart({ labels, values }) {
  const width = 1000;
  const height = 250;
  const padLeft = 10;
  const padBottom = 26;
  const maxY = Math.max(...values) * 1;

  const plotW = width - padLeft;
  const plotH = height - padBottom;
  const stepX = plotW / (labels.length - 1);

  const points = values.map((v, i) => ({
    x: padLeft + i * stepX,
    y: plotH - (v / maxY) * plotH,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${plotH} L ${points[0].x} ${plotH} Z`;

  return (
    <div className="vb-chart-wrap">
      <svg viewBox={`0 0 ${width} ${height + 20}`} className="vb-chart-svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="vbAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={areaPath} fill="url(#vbAreaFill)" />
        <path d={linePath} className="vb-line-path" />

        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="5" className="vb-line-dot" />
        ))}

        {labels.map((l, i) => (
          <text key={l} x={padLeft + i * stepX} y={plotH + 22} className="vb-axis-label" textAnchor="middle">{l}</text>
        ))}
      </svg>
    </div>
  );
}

function DonutChart({ data, centerValue, centerLabel }) {
  const size = 140;
  const cx = size / 2;
  const cy = size / 2;
  const r = 55;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * r;

  const total = data.reduce((sum, d) => sum + parseFloat(d.value.replace(/[^0-9.]/g, '')), 0);
  let cumulative = 0;
  const segments = data.map((d) => {
    const raw = parseFloat(d.value.replace(/[^0-9.]/g, ''));
    const pct = raw / total;
    const length = pct * circumference;
    const offset = cumulative;
    cumulative += length;
    return { ...d, length, offset };
  });

  return (
    <div className="vb-donut-chart">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <g transform={`rotate(-90 ${cx} ${cy})`}>
          {segments.map((s, i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${s.length} ${circumference - s.length}`}
              strokeDashoffset={-s.offset}
            />
          ))}
        </g>
      </svg>
      <div className="vb-donut-center">
        <span className="vb-donut-value">{centerValue}</span>
        <span className="vb-donut-label">{centerLabel}</span>
      </div>
    </div>
  );
}

// ================== MAIN COMPONENT ==================
export default function VoucherBudgetTab() {
  return (
    <div className="vb-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="vb-main">
        <div className="vb-panel">
          {/* 1. Budget Review */}
          <div className="vb-panel-section">
            <div className="vb-card-header">
              <StepBadge n={1} />
              <h3 className="vb-card-title">Budget Review</h3>
            </div>
            <p className="vb-desc">Real-time overview of this voucher's budget and spending.</p>

            <div className="vb-review-grid">
              {budgetReview.map((s) => (
                <div className="vb-review-item" key={s.label}>
                  <span className="vb-review-label">{s.label}</span>
                  <span className="vb-review-value">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="vb-panel-divider" />
          {/* 2. Budget Consumption Progress */}
          <div className="vb-panel-section">
            <div className="vb-card-header">
              <StepBadge n={2} />
              <h3 className="vb-card-title">Budget Consumption Progress</h3>
            </div>
            <p className="vb-desc">Track how much of the budget has been used.</p>

            <div className="vb-progress-track">
              <div className="vb-progress-fill" style={{ width: `${consumption.pct}%` }} />
            </div>
            <div className="vb-progress-labels">
              <span>{consumption.used} used</span>
              <span>{consumption.total} total</span>
            </div>
          </div>
          <div className="vb-panel-divider" />
          {/* 3 & 4. Funding Allocation + Redemption Summary */}
          <div className="vb-panel-section">
            <div className="vb-two-col">
              <div className="vb-col">
                <div className="vb-card-header">
                  <StepBadge n={3} />
                  <h3 className="vb-card-title">Funding Allocation</h3>
                </div>
                <p className="vb-desc">How the budget is funded between platform and provider.</p>

                <div className="vb-funding-row">
                  <DonutChart data={fundingAllocation} centerValue={fundingTotal} centerLabel="EGP" />
                  <div className="vb-funding-legend">
                    {fundingAllocation.map((f) => (
                      <div className="vb-legend-item" key={f.label}>
                        <span className="vb-legend-dot" style={{ backgroundColor: f.color }} />
                        <span className="vb-legend-label">{f.label}</span>
                        <span className="vb-legend-value">{f.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="vb-col">
                <div className="vb-card-header">
                  <StepBadge n={4} />
                  <h3 className="vb-card-title">Redemption Summary</h3>
                </div>
                <p className="vb-desc">Key redemption and discount insights.</p>

                <div className="vb-mini-stats-grid">
                  {redemptionSummary.map((s) => {
                    const Icon = s.icon;
                    return (
                      <div className="vb-mini-stat" key={s.label}>
                        <span className="vb-mini-stat-icon" style={{ backgroundColor: s.iconBg }}>
                          <Icon size={12} color={s.iconColor} />
                        </span>
                        <div className="vb-mini-stat-text">
                          <span className="vb-mini-stat-label">{s.label}</span>
                          <span className="vb-mini-stat-value">{s.value}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="vb-panel-divider" />
          {/* 5. Budget History */}
          <div className="vb-panel-section">
            <div className="vb-card-header-row">
              <div className="vb-card-header">
                <StepBadge n={5} />
                <h3 className="vb-card-title">Budget History</h3>
              </div>
              <button className="vb-select-btn">Last 30 Days</button>
            </div>
            <p className="vb-desc">Daily budget consumption over time.</p>

            <LineChart labels={chartLabels} values={chartValues} />
          </div>
        </div>
      </div>

      {/* ---------- SIDEBAR ---------- */}
      <div className="vb-sidebar">
        {/* Budget Health */}
        <div className="vb-card">
          <h3 className="vb-card-title">Budget Health</h3>
          <div className="vb-health-row">
            <span className="vb-health-icon">
              <CheckCircle2 size={18} color="#fff" />
            </span>
            <span className="vb-health-title">Healthy</span>
          </div>
          <p className="vb-health-line">Your budget is on track</p>
        </div>

        {/* Funding Split */}
        <div className="vb-card">
          <h3 className="vb-card-title">Funding Spilt</h3>
          <div className="vb-split-list">
            {fundingSplit.map((f) => (
              <div className="vb-split-item" key={f.label}>
                <span className="vb-legend-dot" style={{ backgroundColor: f.color }} />
                <span className="vb-split-label">{f.label}</span>
                <span className="vb-split-value">{f.value}</span>
                <span className="vb-split-pct">{f.pct}</span>
              </div>
            ))}
            <div className="vb-split-divider" />
            <div className="vb-split-item">
              <span className="vb-split-label vb-split-total-label">Total</span>
              <span className="vb-split-value">{fundingSplitTotal.value}</span>
              <span className="vb-split-pct">{fundingSplitTotal.pct}</span>
            </div>
          </div>
        </div>

        {/* Budget Rules */}
        <div className="vb-card">
          <h3 className="vb-card-title">Budget Rules</h3>
          <div className="vb-rules-list">
            {budgetRules.map((r, i) => (
              <div className="vb-rule-item" key={r.label}>
                <span className="vb-rule-num">{i + 1}</span>
                <span className="vb-rule-label">{r.label}</span>
                {r.pill ? (
                  <span className="vb-enabled-pill">{r.value}</span>
                ) : (
                  <span className="vb-rule-value">{r.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Alerts & Notifications */}
        <div className="vb-card">
          <h3 className="vb-card-title">Alerts & Notifications</h3>
          <div className="vb-alert-box vb-alert-success">
            <span className="vb-alert-icon vb-alert-icon-success">
              <CheckCircle2 size={14} color="#fff" />
            </span>
            <div className="vb-alert-text">
              <span className="vb-alert-title">No active alerts</span>
              <span className="vb-alert-sub">Everything looks good!</span>
            </div>
          </div>
          <div className="vb-alert-box vb-alert-info">
            <span className="vb-alert-icon vb-alert-icon-info">
              <Info size={14} color="#535353" />
            </span>
            <span className="vb-alert-sub">You will be notified when budget utilization reaches 85%</span>
          </div>
        </div>
      </div>
    </div>
  );
}